/**
 * Common event behavior props for input components.
 * These props control whether to call preventDefault() and stopPropagation()
 * on user interaction events (e.g., click, change).
 *
 * Useful when input components are nested inside other interactive elements
 * (e.g., clickable table rows, draggable containers) where event bubbling
 * needs to be controlled.
 */
export interface EventBehaviorProps {
    /**
     * Whether to call event.preventDefault() on the interaction event.
     * Defaults to false.
     */
    eventPreventDefault?: boolean;
    /**
     * Whether to call event.stopPropagation() on the interaction event.
     * Prevents the event from bubbling up to parent elements.
     * Defaults to false.
     */
    eventStopPropagation?: boolean;
}

/**
 * Default values for event behavior props.
 */
export const DEFAULT_EVENT_BEHAVIOR: Required<EventBehaviorProps> = {
    eventPreventDefault: false,
    eventStopPropagation: false,
};

/**
 * Utility function to handle event behavior based on props.
 * Call this at the beginning of event handlers to apply the configured behavior.
 *
 * @param event - The React event to handle
 * @param props - The event behavior props
 *
 * @example
 * ```tsx
 * const handleClick = (e: React.MouseEvent) => {
 *   handleEventBehavior(e, { eventPreventDefault, eventStopPropagation });
 *   // ... rest of handler
 * };
 * ```
 */
export const handleEventBehavior = <T extends { preventDefault: () => void; stopPropagation: () => void }>(
    event: T,
    props: EventBehaviorProps,
): void => {
    if (props.eventPreventDefault) {
        event.preventDefault();
    }
    if (props.eventStopPropagation) {
        event.stopPropagation();
    }
};
